import { z } from 'zod';
import { roleSchema, type Capability } from './roles.js';

export const loginRequestSchema = z.object({
  email: z.string().email().max(200),
  password: z.string().min(1).max(200),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const sessionOrgSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
});
export type SessionOrg = z.infer<typeof sessionOrgSchema>;

export const sessionUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  phone: z.string().nullable(),
  role: roleSchema,
  orgId: z.string().nullable(),
  /**
   * When the person last proved they read mail at `email` / receive SMS at
   * `phone`. Null means nobody ever did — which is the state every account
   * created before contact verification existed is in.
   *
   * Optional on the schema so a client built against an older API still
   * parses; the API always sends both. See `account-security.ts`.
   */
  emailVerifiedAt: z.number().nullable().optional(),
  phoneVerifiedAt: z.number().nullable().optional(),
  /** Whether this person has a second factor switched on. */
  mfaEnabled: z.boolean().optional(),
});
export type SessionUser = z.infer<typeof sessionUserSchema>;

/**
 * Self-service profile edits — the signed-in user updating their own record.
 *
 * NAME ONLY, deliberately. This route used to write `email` and `phone`
 * straight through, which meant anyone holding an open session could repoint
 * the address the account signs in with, silently, in one request. Both fields
 * now go through `POST /api/auth/me/contact`, which re-authenticates with the
 * password, proves the new value with a code sent to it, and tells the OLD
 * value that a change was asked for. Sending either one here is refused with a
 * message naming that endpoint rather than being quietly ignored — a
 * half-applied profile save is worse than a rejected one.
 *
 * The capability itself is not withdrawn: a person can still change their own
 * email and their own phone, and an admin and a superadmin can change theirs
 * on the same screen under the same rules. What changed is that the change has
 * to be proved.
 */
export const updateProfileSchema = z.object({
  name: z.string().min(1).max(120).optional(),
});
export type UpdateProfileRequest = z.infer<typeof updateProfileSchema>;

export const meResponseSchema = z.object({
  user: sessionUserSchema,
  /** The org the session is currently acting in (superadmin can switch). */
  activeOrg: sessionOrgSchema.nullable(),
  /** Orgs the user may act in. One entry for org users, all orgs for superadmin. */
  orgs: z.array(sessionOrgSchema),
  capabilities: z.array(z.string()),
  /**
   * Whether this platform can take photographs at all.
   *
   * ONE non-secret boolean, and the endpoint is not to be widened past it.
   * `/api/auth/me` is read by every client on every load; anything added here
   * is added to all of them.
   *
   * It exists because the field app has to know whether to offer the camera,
   * and until now the only thing that knew was `GET /api/storage/settings` —
   * superadmin-only, and it returns the S3 secret access key, so an engineer
   * could never call it. Derived from `driverConfigured(readStorageSettings())`
   * against the ACTIVE driver: photographs go to object storage and are never
   * mirrored onto this server, so "S3 credentials are filled in but the local
   * directory is the live store" is false here. That is the difference between
   * the step being DISABLED and it being degraded into writing subscriber
   * photographs onto the API box.
   *
   * Optional on the schema so a client built against an older API still
   * parses; the API always sends it.
   */
  photosEnabled: z.boolean().optional(),
});
export type MeResponse = Omit<z.infer<typeof meResponseSchema>, 'capabilities'> & {
  capabilities: Capability[];
};

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: z.string().min(10).max(200),
});
export type ChangePasswordRequest = z.infer<typeof changePasswordSchema>;
